/**
 * OptimizedImage Component
 * Automatically selects appropriate image size based on context and provides loading optimization
 */

import React, { useState, useMemo } from 'react';
import { cn } from '@/lib/utils';
import { getImageUrlFromAsset } from '@/utils/images';
import { logDebug, logError } from '@/utils/errorHandler';

export type ImageSize = 'thumbnail' | 'medium' | 'full';
export type ImageContext = 'table' | 'hover' | 'detail' | 'gallery' | 'modal';

interface OptimizedImageProps {
  /** Product asset object containing different image URLs */
  asset?: {
    id?: number | string;
    url_thumbnail?: string;
    url_medium?: string;
    url_full?: string;
    file_url?: string;
    url?: string;
    name?: string;
  };
  /** Direct image URLs for different sizes */
  thumbnailUrl?: string;
  mediumUrl?: string;
  fullUrl?: string;
  /** Fallback image URL */
  fallbackUrl?: string;
  /** Alt text for the image */
  alt: string;
  /** Context where the image is being used */
  context?: ImageContext;
  /** Explicit size override */
  size?: ImageSize;
  /** CSS class names */
  className?: string;
  /** Loading behavior */
  loading?: 'lazy' | 'eager';
  /** Click handler */
  onClick?: () => void;
  /** Style object */
  style?: React.CSSProperties;
  /** Whether image is clickable */
  clickable?: boolean;
}

/**
 * Maps context to appropriate image size
 */
const contextToSize: Record<ImageContext, ImageSize> = {
  table: 'thumbnail',
  hover: 'medium',
  detail: 'medium',
  gallery: 'medium',
  modal: 'full',
};

/**
 * OptimizedImage component with automatic size selection and fallbacks
 */
export function OptimizedImage({
  asset,
  thumbnailUrl,
  mediumUrl,
  fullUrl,
  fallbackUrl,
  alt,
  context = 'detail',
  size,
  className,
  loading = 'lazy',
  onClick,
  style,
  clickable = false,
}: OptimizedImageProps) {
  const [imageError, setImageError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Determine the target size
  const targetSize = size || contextToSize[context];

  // Build URL options from asset or direct props
  const imageUrls = useMemo(() => {
    // Use getImageUrlFromAsset for all sizes to ensure consistent S3 handling
    const assetThumbnailUrl = asset ? getImageUrlFromAsset(asset, 'thumbnail') : undefined;
    const assetMediumUrl = asset ? getImageUrlFromAsset(asset, 'medium') : undefined;
    const assetFullUrl = asset ? getImageUrlFromAsset(asset, 'full') : undefined;

    const urls = {
      // Always prefer getImageUrlFromAsset over direct properties for S3 compatibility
      thumbnail: thumbnailUrl || assetThumbnailUrl || asset?.url_thumbnail,
      medium: mediumUrl || assetMediumUrl || asset?.url_medium,
      full: fullUrl || assetFullUrl || asset?.url_full,
    };

    // Debug logging for troubleshooting Assets tab image display
    logDebug('OptimizedImage URL resolution', {
      assetId: asset?.id,
      context,
      targetSize,
      getImageUrlResults: { assetThumbnailUrl, assetMediumUrl, assetFullUrl },
      directAssetUrls: {
        url_thumbnail: asset?.url_thumbnail,
        url_medium: asset?.url_medium,
        url_full: asset?.url_full,
      },
      finalUrls: urls,
    });

    // Clean up URLs (remove undefined values)
    return Object.fromEntries(Object.entries(urls).filter(([_, url]) => url)) as Record<
      string,
      string
    >;
  }, [asset, thumbnailUrl, mediumUrl, fullUrl, context, targetSize]);

  // Select the best available image URL with fallbacks
  const selectedImageUrl = useMemo(() => {
    if (imageError && fallbackUrl) {
      logDebug('Using fallback URL due to error', { fallbackUrl, assetId: asset?.id });
      return fallbackUrl;
    }

    let selectedUrl: string | undefined;

    // Try target size first
    if (imageUrls[targetSize]) {
      selectedUrl = imageUrls[targetSize];
      logDebug('Using target size', { targetSize, selectedUrl, assetId: asset?.id });
    } else {
      // Fallback strategy based on target size
      if (targetSize === 'thumbnail') {
        selectedUrl = imageUrls.medium || imageUrls.full;
        logDebug('Thumbnail fallback', { selectedUrl, assetId: asset?.id });
      } else if (targetSize === 'medium') {
        selectedUrl = imageUrls.full || imageUrls.thumbnail;
        logDebug('Medium fallback', { selectedUrl, assetId: asset?.id });
      } else {
        // full
        selectedUrl = imageUrls.medium || imageUrls.thumbnail;
        logDebug('Full fallback', { selectedUrl, assetId: asset?.id });
      }
    }

    // Return the selected URL with fallbacks
    if (selectedUrl) {
      return selectedUrl;
    }

    // Final fallback: try any available URL
    const anyAvailableUrl = imageUrls.medium || imageUrls.full || imageUrls.thumbnail;
    logDebug('Using any available URL as final fallback', {
      anyAvailableUrl,
      assetId: asset?.id,
    });
    return anyAvailableUrl;
  }, [imageUrls, targetSize, imageError, fallbackUrl, asset?.id]);

  // Generate default placeholder
  const placeholderUrl =
    'data:image/svg+xml;charset=UTF-8,%3Csvg%20width%3D%22200%22%20height%3D%22200%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%20200%20200%22%3E%3Cpath%20fill%3D%22%23f0f0f0%22%20d%3D%22M0%200h200v200H0z%22%2F%3E%3Ctext%20x%3D%22100%22%20y%3D%22100%22%20text-anchor%3D%22middle%22%20dy%3D%22.3em%22%20fill%3D%22%23999%22%20font-family%3D%22system-ui%22%20font-size%3D%2214%22%3ENo%20Image%3C%2Ftext%3E%3C%2Fsvg%3E';

  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    logDebug('Image loaded successfully', {
      src: e.currentTarget.src,
      assetId: asset?.id,
    });
    setIsLoading(false);
  };

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    logError(
      'Image failed to load',
      {
        component: 'OptimizedImage',
        action: 'handleImageError',
        metadata: {
          src: e.currentTarget.src,
          assetId: asset?.id,
          error: e,
        },
      },
      {
        showToast: false, // Don't show toast for image loading errors - handled by UI
        userFacing: false, // This is more of a technical issue
      }
    );
    setImageError(true);
    setIsLoading(false);
  };

  const imageUrl = selectedImageUrl || placeholderUrl;

  // Debug what URL is actually being used
  logDebug('Rendering with URL', {
    assetId: asset?.id,
    context,
    imageUrl,
    isPlaceholder: imageUrl === placeholderUrl,
  });

  return (
    <div
      className={cn('relative overflow-hidden', clickable && 'cursor-pointer', className)}
      onClick={onClick}
      style={style}
    >
      {/* Loading skeleton */}
      {isLoading && (
        <div
          className={cn(
            'absolute inset-0 bg-gray-200 animate-pulse flex items-center justify-center',
            'text-gray-400 text-xs'
          )}
        >
          Loading...
        </div>
      )}

      {/* Main image */}
      <img
        src={imageUrl}
        alt={alt}
        loading={loading}
        onLoad={handleImageLoad}
        onError={handleImageError}
        className={cn(
          'w-full h-full transition-opacity duration-200',
          context === 'modal' ? 'object-contain' : 'object-cover',
          isLoading ? 'opacity-0' : 'opacity-100',
          clickable && 'hover:opacity-90'
        )}
        draggable={false}
      />

      {/* Error state overlay */}
      {imageError && !fallbackUrl && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
          <div className="text-center text-xs text-gray-400">
            <div className="mb-1">⚠️</div>
            <div>Image not found</div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Hook to get optimized image URLs from asset
 */
export function useOptimizedImage(asset?: OptimizedImageProps['asset']) {
  return useMemo(() => {
    // Use getImageUrlFromAsset for all sizes to ensure consistent S3 handling
    const thumbnailUrl = asset ? getImageUrlFromAsset(asset, 'thumbnail') : undefined;
    const mediumUrl = asset ? getImageUrlFromAsset(asset, 'medium') : undefined;
    const fullUrl = asset ? getImageUrlFromAsset(asset, 'full') : undefined;

    return {
      // Always prefer getImageUrlFromAsset over direct properties for S3 compatibility
      thumbnail: thumbnailUrl || asset?.url_thumbnail,
      medium: mediumUrl || asset?.url_medium,
      full: fullUrl || asset?.url_full,
      hasImage: !!(
        thumbnailUrl ||
        mediumUrl ||
        fullUrl ||
        asset?.url_thumbnail ||
        asset?.url_medium ||
        asset?.url_full
      ),
    };
  }, [asset]);
}
